import { Callout, HelpLink, Related, ScreenLink } from "./parts";

export function AwsCosts() {
  return (
    <>
      <p>
        AWS cost is what customers' Bedrock usage cost AWS, per customer and month. Nexus can measure it two ways, and{" "}
        <ScreenLink href="/aws-costs">AWS costs</ScreenLink> is where you choose, connect the customers' Cost and Usage
        Reports (CUR), and freeze each month's figure.
      </p>

      <h2 id="expense-basis">Cost basis</h2>
      <table>
        <thead>
          <tr>
            <th>Cost basis</th>
            <th>Where the number comes from</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>AWS list price</td>
            <td>
              Tokens × the published price in <ScreenLink href="/model-prices">Model prices</ScreenLink>. Known the moment
              usage is recorded.
            </td>
          </tr>
          <tr>
            <td>Actual AWS bill (CUR)</td>
            <td>
              The customer account's Cost and Usage Report, shared out over the customer's metered tokens by account, day,
              model and token type. Discounts included, tax excluded; credits and refunds kept as whole-month rows.
            </td>
          </tr>
        </tbody>
      </table>
      <ul>
        <li>There is a fleet default, and each customer can follow it or have its own. Both change from a month.</li>
        <li>A customer can use the actual AWS bill only with a verified CUR source.</li>
        <li>
          Changing the cost basis changes how profit is measured. What a customer is billed depends on their rate plan's
          cost basis, not this one.
        </li>
      </ul>
      <p>
        Example: $1,000 of tokens at list price, with a 10 % private discount on the customer's AWS account, is $900 on the
        actual bill. Profit looks $100 higher on the actual basis.
      </p>

      <h2 id="cur-sources">Connecting a Cost and Usage Report</h2>
      <ol>
        <li>
          An installer operator links the customer's AWS account with the <strong>cur</strong> capability, stack in{" "}
          <code>us-east-1</code> (AWS Data Exports exists only there). See{" "}
          <HelpLink to="installer" section="linking-an-account" />.
        </li>
        <li>
          Add a CUR source for the customer: the linked account, and the bucket, prefix, export name and region the stack
          created (<code>nexus-cur-&lt;link id&gt;-&lt;account&gt;</code>, <code>cur</code>, <code>nexus-cur</code>,{" "}
          <code>us-east-1</code>).
        </li>
        <li>
          <strong>Verify</strong>. The worker checks it in the background; the source becomes active, or shows an error such
          as access denied.
        </li>
        <li>From then on the worker reads it on a schedule. <strong>Ingest now</strong> moves the next read forward.</li>
      </ol>
      <p>
        Disabling a source (through the API) stops it for good; its history stays, and it no longer counts as verified.
      </p>

      <h2 id="usage-types">Usage-type mappings</h2>
      <p>
        The CUR names Bedrock charges by AWS usage type, such as <code>USE1-…-input-tokens</code>. A mapping says which
        model and token type each usage type is. A leading <code>*-</code> matches any region.
      </p>
      <ul>
        <li>Only <strong>verified</strong> mappings are used. Verifying one, or deleting a verified one, needs a billing admin.</li>
        <li>
          Cost under a usage type with no mapping is never guessed. It blocks finalizing the month until it is mapped,
          usually because AWS started billing a new model or region.
        </li>
      </ul>

      <h2 id="finalizing">Finalizing a month</h2>
      <p>
        <strong>Finalize</strong> freezes a customer's AWS cost for a closed month. For a customer whose rate plan is on the
        actual AWS bill, the same step writes the month's invoice. A billing admin finalizes after checking the month's
        reconciliation: whether AWS has marked the bill final, how the cost was shared out, and any unmapped rows.
      </p>
      <table>
        <thead>
          <tr>
            <th>Refused because</th>
            <th>Force allowed?</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>The month is not closed</td>
            <td>No</td>
          </tr>
          <tr>
            <td>An earlier month is still waiting for its invoice</td>
            <td>No: finalize that one first</td>
          </tr>
          <tr>
            <td>A later month already has its invoice</td>
            <td>No</td>
          </tr>
          <tr>
            <td>The plan uses the actual bill but the customer's cost basis is list price</td>
            <td>Yes</td>
          </tr>
          <tr>
            <td>AWS has not marked the month final, or a needed cost is missing</td>
            <td>Yes</td>
          </tr>
          <tr>
            <td>Unmapped usage types with cost</td>
            <td>Yes</td>
          </tr>
        </tbody>
      </table>
      <p>
        A forced finalize needs a reason and prices any missing cost at list price. AWS marks a month's bill final some days
        after it ends, so waiting is usually better than forcing.
      </p>
      <Callout>
        AWS sometimes restates a month after it was finalized. The frozen figure and any invoice never change; a billing
        admin can restate the month through the API, which adds a new revision and carries the difference to the customer's
        next invoice.
      </Callout>

      <Related to={["pricing-and-markup", "revenue-and-margin", "reconciliation", "installer"]} />
    </>
  );
}
